import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { LIVE_DATA_TYPES } from "@/lib/widgets";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, CheckCircle2, ChevronRight, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-6 text-center" data-testid="widget-empty">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Inbox className="h-4 w-4" />
      </span>
      <p className="max-w-[26ch] text-xs text-muted-foreground">{message}</p>
    </div>
  );
}

function ListRows({ items, render }) {
  return (
    <ul className="divide-y divide-border">
      {items.map((it, i) => (
        <li key={i} className="density-row flex items-center gap-2 text-sm">{render(it)}</li>
      ))}
    </ul>
  );
}

export function WidgetContent({ widget, instance }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(LIVE_DATA_TYPES.includes(widget.dataType));

  useEffect(() => {
    if (!LIVE_DATA_TYPES.includes(widget.dataType)) return;
    let alive = true;
    (async () => {
      try {
        const limit = instance?.config?.count || 5;
        const d = await apiFetch(`/widgets/data?type=${widget.dataType}&limit=${limit}`);
        if (alive) setData(d);
      } catch { /* keep empty */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [widget.dataType, instance?.config?.count]);

  if (loading) {
    return <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>;
  }

  // live widgets
  if (widget.dataType === "system_health") {
    const ok = data?.database;
    return (
      <div className="space-y-2 py-1" data-testid="widget-system-health">
        {[["Database", data?.database], ["Authentication", data?.auth]].map(([label, up]) => (
          <div key={label} className="density-row flex items-center justify-between rounded-lg bg-muted/40 text-sm">
            <span className="text-foreground">{label}</span>
            <span className={cn("flex items-center gap-1.5 text-xs font-medium", up ? "text-primary" : "text-destructive")}>
              <span className={cn("h-2 w-2 rounded-full", up ? "bg-primary" : "bg-destructive")} /> {up ? "Operational" : "Down"}
            </span>
          </div>
        ))}
        <p className="pt-1 text-[11px] text-muted-foreground">Supabase Postgres · live</p>
      </div>
    );
  }

  if (widget.dataType === "team_members") {
    const items = data?.items || [];
    if (!items.length) return <EmptyState message={widget.empty} />;
    return (
      <>
        <div className="mb-1 text-xs text-muted-foreground">{data.count} active member{data.count === 1 ? "" : "s"}</div>
        <ListRows items={items} render={(it) => (
          <>
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
              {(it.name || "?").slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{it.name}</span>
            <span className="text-xs text-muted-foreground">{it.role}</span>
          </>
        )} />
      </>
    );
  }

  if (widget.dataType === "recent_activity") {
    const items = data?.items || [];
    if (!items.length) return <EmptyState message={widget.empty} />;
    return (
      <ListRows items={items} render={(it) => (
        <>
          <span className="min-w-0 flex-1 truncate text-foreground">
            <span className="font-medium">{it.actor}</span>{" "}
            <span className="text-muted-foreground">{it.action?.toLowerCase()} {it.entity}</span>
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        </>
      )} />
    );
  }

  // task widgets (real per-user data)
  if (["my_tasks", "today", "assigned_to_me", "reminders", "waiting", "overdue"].includes(widget.dataType)) {
    const items = data?.items || [];
    if (!items.length) return <EmptyState message={widget.empty} />;
    return (
      <>
        <div className="mb-1 text-xs text-muted-foreground">{data.count} item{data.count === 1 ? "" : "s"}</div>
        <ListRows items={items} render={(it) => (
          <>
            <span className={cn("h-2 w-2 shrink-0 rounded-full",
              it.priority === "urgent" ? "bg-destructive" : it.priority === "high" ? "bg-warning" : "bg-muted-foreground/40")} />
            <span className="min-w-0 flex-1 truncate text-foreground">{it.title}</span>
            {it.waiting && <span className="text-[10px] text-warning">Waiting</span>}
            {it.due && <span className="text-xs text-muted-foreground">{it.due}</span>}
          </>
        )} />
      </>
    );
  }

  // time widgets (real per-user data — Phase B)
  if (widget.dataType === "my_time_today") {
    const mins = data?.minutes || 0;
    const items = data?.items || [];
    return (
      <div className="space-y-2 py-1" data-testid="widget-my-time-today">
        <div className="text-2xl font-heading font-semibold text-foreground">{fmtMins(mins)}</div>
        <div className="text-xs text-muted-foreground">tracked today</div>
        {items.length > 0 && <ListRows items={items} render={(it) => (
          <><span className="min-w-0 flex-1 truncate text-foreground">{it.activity}</span><span className="text-xs text-muted-foreground">{fmtMins(it.minutes)}</span></>
        )} />}
      </div>
    );
  }
  if (widget.dataType === "weekly_progress") {
    return (
      <div className="grid grid-cols-2 gap-3 py-1" data-testid="widget-weekly-progress">
        <div className="rounded-lg bg-muted/40 p-3">
          <div className="text-2xl font-heading font-semibold text-foreground">{data?.tasks_completed || 0}</div>
          <div className="text-xs text-muted-foreground">tasks completed</div>
        </div>
        <div className="rounded-lg bg-muted/40 p-3">
          <div className="text-2xl font-heading font-semibold text-foreground">{fmtMins(data?.tracked_minutes || 0)}</div>
          <div className="text-xs text-muted-foreground">time tracked</div>
        </div>
      </div>
    );
  }

  // all content-driven widgets are empty in Release 1
  return <EmptyState message={widget.empty} />;
}

function fmtMins(m) {
  const h = Math.floor((m || 0) / 60); const mm = Math.round((m || 0) % 60);
  return h > 0 ? (mm > 0 ? `${h}h ${mm}m` : `${h}h`) : `${mm}m`;
}
